import { createFileRoute, Link } from '@tanstack/react-router'
import { Button } from '@/components/ui/button'
import { ArrowRight } from 'lucide-react'
import { itemList, jsonLdScript } from '@/lib/schema'

export const Route = createFileRoute('/_site/alternatives/')({
  component: AlternativesIndexPage,
  head: () => ({
    meta: [
      { title: 'Monitoring Tool Alternatives — Compare Top Options | Uptrack' },
      { name: 'description', content: 'Find the best alternatives to popular uptime monitoring tools. Honest, side-by-side comparisons of features, pricing, and trade-offs.' },
      { property: 'og:title', content: 'Monitoring Tool Alternatives — Compare Top Options | Uptrack' },
      { property: 'og:description', content: 'Find the best alternatives to UptimeRobot, Pingdom, Datadog, and more. Honest comparisons.' },
      { property: 'og:image', content: 'https://uptrack.app/og/alternatives.png' },
      { name: 'twitter:card', content: 'summary_large_image' },
    ],
    links: [
      { rel: 'canonical', href: 'https://uptrack.app/alternatives' },
    ],
    scripts: [
      jsonLdScript({
        '@context': 'https://schema.org',
        '@type': 'CollectionPage',
        name: 'Monitoring Tool Alternatives',
        description: 'Find the best alternatives to popular uptime monitoring tools.',
        url: 'https://uptrack.app/alternatives',
      }),
      jsonLdScript(itemList(
        'Monitoring Tool Alternatives',
        tools.map((t) => ({ name: t.name, url: `https://uptrack.app${t.href}`, description: t.description })),
        'https://uptrack.app/alternatives',
      )),
    ],
  }),
})

const tools = [
  { name: 'UptimeRobot', href: '/alternatives/uptimerobot', description: 'Free plan checks every 5 minutes; 30s checks start on Team ($41/mo). Uptrack checks every 30s from $3/mo.' },
  { name: 'Pingdom', href: '/alternatives/pingdom', description: 'From SolarWinds, priced by number of checks. Uptrack covers uptime and browser checks at a flat rate.' },
  { name: 'Datadog', href: '/alternatives/datadog', description: 'A full observability suite. Uptrack focuses on uptime + browser checks at a lower price.' },
  { name: 'Better Stack', href: '/alternatives/betterstack', description: 'Uptime, on-call, and logs, priced per responder. Uptrack is a focused option with flat plans.' },
  { name: 'StatusCake', href: '/alternatives/statuscake', description: 'An established option. Uptrack adds 30-second checks and browser flows.' },
  { name: 'Pulsetic', href: '/alternatives/pulsetic', description: 'Status pages with a free plan of 10 monitors. Uptrack has 30-second checks on every plan.' },
  { name: 'Site24x7', href: '/alternatives/site24x7', description: 'A broad suite with many plan tiers. Uptrack uses flat, simple plans.' },
  { name: 'New Relic', href: '/alternatives/newrelic', description: 'Billed per user and usage past the free tier. Uptrack has flat plans.' },
  { name: 'Freshping', href: '/alternatives/freshping', description: 'Shut down in March 2026. Uptrack offers 30s checks, alert confirmation, and status pages.' },
  { name: 'Uptime Kuma', href: '/alternatives/uptime-kuma', description: 'Open-source and self-hosted. Uptrack is hosted, so checks run independently of your own infrastructure.' },
  { name: 'HetrixTools', href: '/alternatives/hetrixtools', description: 'Budget uptime, blacklist, and server monitoring at 1-minute checks. Uptrack adds 30s and browser checks.' },
  { name: 'Checkly', href: '/alternatives/checkly', description: 'Developer-first and strong for complex browser checks. Uptrack is simpler and cheaper for uptime + basic flows.' },
  { name: 'Hyperping', href: '/alternatives/hyperping', description: '30-second checks start at $24/mo. Uptrack Pro starts at $3/mo with 30-second checks.' },
  { name: 'Cronitor', href: '/alternatives/cronitor', description: 'Cron and uptime monitoring billed per monitor. Uptrack includes both in flat plans.' },
  { name: 'Grafana Cloud', href: '/alternatives/grafana', description: 'A full observability stack with k6-based checks. Turnkey alternatives need no scripts.' },
]

function AlternativesIndexPage() {
  return (
    <>
      <section>
        <div className="container mx-auto px-4 py-12 md:py-16">
          <p className="mb-4 text-sm text-muted-foreground">15 tools compared</p>
          <h1 className="text-[2.25rem] font-semibold leading-[1.1] tracking-tight md:text-[3rem]">Monitoring Tool Alternatives</h1>
          <p className="mt-5 max-w-2xl text-lead text-body">
            Honest, side-by-side comparisons of the top uptime monitoring tools. Find the best alternative for your needs and budget.
          </p>
      <div className="mt-10 grid grid-cols-1 gap-4 sm:grid-cols-2">
        {tools.map((t) => (
          <Link
            key={t.href}
            to={t.href}
            className="group flex items-start justify-between gap-4 rounded-lg border border-border bg-card p-5 transition-colors hover:border-primary"
          >
            <div>
              <h2 className="text-[17px] font-semibold text-foreground group-hover:text-primary">Best {t.name} Alternatives</h2>
              <p className="mt-1.5 text-[15px] leading-relaxed text-body">{t.description}</p>
            </div>
            <ArrowRight aria-hidden="true" className="mt-1 h-4 w-4 shrink-0 text-muted-foreground transition-colors group-hover:text-primary" />
          </Link>
        ))}
      </div>
        </div>
      </section>

      <section className="border-y border-border bg-card">
        <div className="container mx-auto grid grid-cols-1 items-center gap-6 px-4 py-12 md:py-[72px] lg:grid-cols-12">
          <div className="lg:col-span-8">
            <h2 className="text-[1.75rem] font-semibold leading-tight tracking-tight md:text-headline">Try Uptrack</h2>
            <p className="mt-3 max-w-xl text-lead text-body">Choose Pro, Team, or Business for 30-second monitoring and alerts.</p>
          </div>
          <div className="lg:col-span-4 lg:text-right">
            <Button size="lg" className="h-11 px-5 text-[15px]" asChild>
              <Link to="/pricing">
                See plans
                <ArrowRight className="ml-1 h-4 w-4" aria-hidden="true" />
              </Link>
            </Button>
            <p className="mt-3 text-sm text-muted-foreground">from $3/mo · 30-day money-back guarantee</p>
          </div>
        </div>
      </section>
    </>
  )
}
